import { BILLING_CYCLE, NO_NEED_CHANGE_PRICE_CYCLE } from '@/common/constants';
import { translation } from '@/plugins/i18n';
import { transferMoney } from '@/utils/money';
import { capacityFormatter } from '@/common/utils';
import { logger } from '@/utils/log';

/**
 * Check if a plan type is Zoom Phone
 *
 * This is a lightweight utility function extracted here to avoid circular dependencies.
 * Originally in @/common/service/computers/plans/pbx.js, but that file has heavy dependencies
 * (Vuex store, Zoom Phone constants) which caused module initialization issues for early-loading
 * modules like src/plans/<plan_name>/pre.js that depend on product-unit.js.
 *
 * @param {string} planType - Plan type string (e.g., "pbx", "pbx$zpi", "pbx$zpa")
 * @returns {boolean} True if the plan type is a Zoom Phone plan
 *
 * @example
 * isZoomPhone('pbx') // true
 * isZoomPhone('pbx$zpi') // true (Zoom Phone add-on)
 * isZoomPhone('zoom_meeting') // false
 */
export function isZoomPhone(planType = "") {
  if (!planType) return false;
  const [type] = planType.split('$');
  return type === 'pbx';
}

export const planUnitMap = {
  user: 0,
  license: 1,
  attendee: 2,
  room: 3,
  port: 4,
  account: 4,
  membership: 5,
  credits: 6,
  number: 7,
  space: 8,
  calling: 9,
  hub_host: 10,
  co_editor: 11,
  localNumber: 12,
  tollFreeNumber: 13,
  participant: 14,
  host: 15,
  /** Per-minute unit (e.g. Zoom Virtual Agent Receptionist). Display as "/min". */
  perMinute: 16,
  // Alias mappings for planTypes - these allow direct lookup by planType string
  additional_host: 10,      // -> hub_host
  additional_co_editor: 11, // -> co_editor
  host_or_room: 12, // -> host or room
};

export const getPlanUnitWithCycle = (billingCycle = '', type = planUnitMap.user) => {
  if (type === planUnitMap.user) {
    switch (billingCycle) {
      case BILLING_CYCLE.ANNUAL:
        return translation('billing.common.per_billing_cycle_user.annual');
      case BILLING_CYCLE.ANNUAL_TERM_MONTHLY:
        return translation('billing.common.per_billing_cycle_user.monthly');
      case BILLING_CYCLE.MONTHLY:
        return translation('billing.common.per_billing_cycle_user.monthly');
      case BILLING_CYCLE.ONE_MONTH:
      case BILLING_CYCLE.ONE_TIME:
        return translation('billing.common.per_billing_cycle_user.one_month');
      case BILLING_CYCLE.QUARTERLY:
        return translation('billing.common.per_billing_cycle_user.quarterly');
      case BILLING_CYCLE.TWO_YEARS:
        return translation('billing.common.per_billing_cycle_user.two_years');
      case BILLING_CYCLE.THREE_YEARS:
        return translation('billing.common.per_billing_cycle_user.three_years');
      default:
        return "";
    }
  } else if (type === planUnitMap.license) {
    switch (billingCycle) {
      case BILLING_CYCLE.ANNUAL:
        return translation('billing.common.per_billing_cycle_license.annual');
      case BILLING_CYCLE.MONTHLY:
        return translation('billing.common.per_billing_cycle_license.monthly');
      case BILLING_CYCLE.ONE_MONTH:
      case BILLING_CYCLE.ONE_TIME:
        return translation('billing.common.per_billing_cycle_license.one_month');
      case BILLING_CYCLE.QUARTERLY:
        return translation('billing.common.per_billing_cycle_license.quarterly');
      case BILLING_CYCLE.TWO_YEARS:
        return translation('billing.common.per_billing_cycle_license.two_years');
      case BILLING_CYCLE.THREE_YEARS:
        return translation('billing.common.per_billing_cycle_license.three_years');
      default:
        return "";
    }
  } else if (type === planUnitMap.room) {
    switch (billingCycle) {
      case BILLING_CYCLE.ANNUAL:
        return translation('billing.common.per_billing_cycle_room.annual');
      case BILLING_CYCLE.MONTHLY:
        return translation('billing.common.per_billing_cycle_room.monthly');
      case BILLING_CYCLE.ONE_MONTH:
        return translation('billing.common.per_billing_cycle_room.one_month');
      case BILLING_CYCLE.QUARTERLY:
        return translation('billing.common.per_billing_cycle_room.quarterly');
      case BILLING_CYCLE.TWO_YEARS:
        return translation('billing.common.per_billing_cycle_room.two_years');
      case BILLING_CYCLE.THREE_YEARS:
        return translation('billing.common.per_billing_cycle_room.three_years');
      default:
        return "";
    }
  } else if (type === planUnitMap.port) {
    switch (billingCycle) {
      case BILLING_CYCLE.ANNUAL:
        return translation('billing.common.per_billing_cycle_port.annual');
      case BILLING_CYCLE.MONTHLY:
        return translation('billing.common.per_billing_cycle_port.monthly');
      case BILLING_CYCLE.ONE_MONTH:
        return translation('billing.common.per_billing_cycle_port.one_month');
      case BILLING_CYCLE.QUARTERLY:
        return translation('billing.common.per_billing_cycle_port.quarterly');
      case BILLING_CYCLE.TWO_YEARS:
        return translation('billing.common.per_billing_cycle_port.two_years');
      case BILLING_CYCLE.THREE_YEARS:
        return translation('billing.common.per_billing_cycle_port.three_years');
      default:
        return "";
    }
  } else if (type === planUnitMap.credits) {
    switch (billingCycle) {
      case BILLING_CYCLE.ANNUAL:
        return translation('billing.common.per_billing_cycle.annual');
      case BILLING_CYCLE.QUARTERLY:
        return translation('billing.common.per_billing_cycle.quarterly');
      case BILLING_CYCLE.TWO_YEARS:
        return translation('billing.common.per_billing_cycle.two_years');
      case BILLING_CYCLE.THREE_YEARS:
        return translation('billing.common.per_billing_cycle.three_years');
      case BILLING_CYCLE.MONTHLY:
      case BILLING_CYCLE.ONE_MONTH:
      default:
        return translation('billing.common.per_billing_cycle.monthly');
    }
  } else if (type === planUnitMap.number) {
    switch (billingCycle) {
      case BILLING_CYCLE.ANNUAL:
        return translation('billing.common.per_billing_cycle_number.annual');
      case BILLING_CYCLE.MONTHLY:
        return translation('billing.common.per_billing_cycle_number.monthly');
      case BILLING_CYCLE.ONE_MONTH:
        return translation('billing.common.per_billing_cycle_number.one_month');
      case BILLING_CYCLE.QUARTERLY:
        return translation('billing.common.per_billing_cycle_number.quarterly');
      case BILLING_CYCLE.TWO_YEARS:
        return translation('billing.common.per_billing_cycle_number.two_years');
      case BILLING_CYCLE.THREE_YEARS:
        return translation('billing.common.per_billing_cycle_number.three_years');
      default:
        return "";
    }
  } else if (type === planUnitMap.space) {
    switch (billingCycle) {
      case BILLING_CYCLE.ANNUAL:
        return translation('billing.common.per_billing_cycle_space.annual');
      case BILLING_CYCLE.MONTHLY:
        return translation('billing.common.per_billing_cycle_space.monthly');
      case BILLING_CYCLE.QUARTERLY:
        return translation('billing.common.per_billing_cycle_space.quarterly');
      case BILLING_CYCLE.TWO_YEARS:
        return translation('billing.common.per_billing_cycle_space.two_years');
      case BILLING_CYCLE.THREE_YEARS:
        return translation('billing.common.per_billing_cycle_space.three_years');
      default:
        return "";
    }
  } else if (type === planUnitMap.perMinute) {
    return translation('billing.common.per_minute');
  }

  return '';
};

// Only need show monthly text
export const getDiscountedMonthPlanUnit = (type = planUnitMap.user) => {
  switch (type) {
    case planUnitMap.user:
      return translation('billing.common.per_billing_cycle_user.monthly');
    case planUnitMap.license:
      return translation('billing.common.per_billing_cycle_license.monthly');
    case planUnitMap.room:
      return translation('billing.common.per_billing_cycle_room.monthly');
    case planUnitMap.port:
      return translation('billing.common.per_billing_cycle_port.monthly');
    case planUnitMap.credits:
      return translation('billing.common.per_billing_cycle.monthly');
    case planUnitMap.number:
      return translation('billing.common.per_billing_cycle_number.monthly');
    case planUnitMap.space:
      return translation('billing.common.per_billing_cycle_space.monthly');
    case planUnitMap.perMinute:
      return translation('billing.common.per_minute');
    default:
      return ''
  }
};

// update it if you need
export const getPerMonthPlanUnit = (type = planUnitMap.user) => {
  switch (type) {
    case planUnitMap.user:
      return translation('billing.common.per_month_unit.user');
    default:
      return ''
  }
};
export const getPlanUnitWithQuantity = (type = planUnitMap.user) => {
  switch (type) {
    case planUnitMap.license:
      return translation('billing.planform.unit.license');
    case planUnitMap.user:
      return translation('billing.planform.unit.user');
    case planUnitMap.room:
      return translation('billing.planform.unit.room');
    case planUnitMap.port:
      return translation('billing.planform.unit.port');
    case planUnitMap.account:
      return translation('billing.planform.unit.account');
    case planUnitMap.membership:
      return translation('billing.planform.unit.membership');
    case planUnitMap.credits:
      return translation('billing.planform.unit.credits');
    case planUnitMap.number:
      return translation('billing.planform.unit.number');
    case planUnitMap.space:
      return translation('billing.planform.unit.space');
    case planUnitMap.calling:
      return translation('billing.pbx.x_calling_plan');
    case planUnitMap.perMinute:
      return translation('billing.common.per_minute');
    default:
      return "";
  }
}

export const getBillTextByCycle = (billingCycle) => {
  if (NO_NEED_CHANGE_PRICE_CYCLE.includes(billingCycle)) {
    return translation('billing.bill_monthly')
  }

  switch (billingCycle) {
    case BILLING_CYCLE.ANNUAL_TERM_MONTHLY:
      return translation('billing.bill_monthly')
    case BILLING_CYCLE.ANNUAL:
      return translation('billing.bill_annually')
    case BILLING_CYCLE.QUARTERLY:
      return translation('billing.bill_quarterly')
    case BILLING_CYCLE.TWO_YEARS:
      return translation('billing.bill_two_years')
    case BILLING_CYCLE.THREE_YEARS:
      return translation('billing.bill_three_years')
  }

  return '';
}

export const getPlanPerUnitDesc = (unit) => {
  switch (unit) {
    case planUnitMap.user:
      return translation('billing.common.per_billing_cycle_user.one_month');
    case planUnitMap.license:
      return translation('billing.common.per_billing_cycle_license.one_month');
    case planUnitMap.room:
      return translation('billing.common.per_billing_cycle_room.one_month');
    case planUnitMap.port:
      return translation('billing.common.per_billing_cycle_port.one_month');
    case planUnitMap.number:
      return translation('billing.common.per_billing_cycle_number.one_month');
    case planUnitMap.attendee:
      return translation('billing.common.per_billing_cycle_attendee.one_month');
    case planUnitMap.credits:
      return translation('billing.common.per_billing_cycle.one_month');
    case planUnitMap.space:
      return translation('billing.common.per_billing_cycle_space.one_month');
    case planUnitMap.perMinute:
      return translation('billing.common.per_minute');
    default:
      return translation('billing.common.per_billing_cycle_user.one_month');
  }
}


/**
 * Generic helper to get billing cycle text with validation
 * @param {string} billingCycle - Billing cycle enum value
 * @param {Object} mapping - Billing cycle to i18n key mapping
 * @param {string} functionName - Function name for logging
 * @returns {string} Translated billing cycle text or empty string
 * @private
 */
function getBillingCycleTextWithValidation(billingCycle, mapping, functionName) {
  // Validate mapping completeness (only in development)
  if (process.env.NODE_ENV === 'development') {
    validateBillingCycleMapping(functionName, mapping);
  }

  const i18nKey = mapping[billingCycle];

  if (!i18nKey) {
    logger.warn(`[${functionName}] No i18n key mapping found for billingCycle: "${billingCycle}"`);
    return '';
  }

  const translated = translation(i18nKey);

  // Check if translation failed (returns the key itself)
  if (translated === i18nKey) {
    logger.warn(`[${functionName}] Missing i18n translation for key: "${i18nKey}" (billingCycle: "${billingCycle}")`);
    return '';
  }

  return translated;
}

/**
 * Validate that all BILLING_CYCLE enum values have corresponding mappings
 * Logs warnings for missing mappings in development mode
 * @param {string} functionName - Name of the function being validated
 * @param {Object} mapping - The billing cycle to i18n key mapping object
 * @private
 */
function validateBillingCycleMapping(functionName, mapping) {
  // Get all BILLING_CYCLE enum values
  const allBillingCycles = Object.values(BILLING_CYCLE);
  const mappedBillingCycles = Object.keys(mapping);

  // Find missing mappings
  const missingMappings = allBillingCycles.filter(
    (cycle) => !mappedBillingCycles.includes(cycle)
  );

  if (missingMappings.length > 0) {
    logger.warn(
      `[${functionName}] Missing billing cycle mappings detected!\n` +
      `Please add i18n key mappings for the following billing cycles:\n` +
      `${missingMappings.map((cycle) => `  - BILLING_CYCLE: "${cycle}"`).join('\n')}\n` +
      `This may cause display issues when these billing cycles are used.`
    );
  }
}

/**
 * Get billing cycle text in simple lowercase format (e.g., "monthly", "annual")
 * Used in banners, simple descriptions, and plan comparisons
 * @param {string} billingCycle - Billing cycle enum value
 * @returns {string} Translated billing cycle text or empty string
 *
 * @example
 * getBillingCycleText('monthly')  // Returns: "monthly"
 * getBillingCycleText('annual')   // Returns: "annual"
 * getBillingCycleText('invalid')  // Returns: "" (with warning in console)
 */
export const getBillingCycleText = (billingCycle) => {
  const billingCycleTextMap = {
    [BILLING_CYCLE.MONTHLY]: 'billing.billing_cycle_in_terms.monthly',
    [BILLING_CYCLE.ANNUAL]: 'billing.billing_cycle_in_terms.annual',
    [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: 'billing.billing_cycle_in_terms.annual_term_monthly',
    [BILLING_CYCLE.ONE_MONTH]: 'billing.billing_cycle_in_terms.one_month',
    [BILLING_CYCLE.QUARTERLY]: 'billing.billing_cycle_in_terms.quarterly',
    [BILLING_CYCLE.TWO_YEARS]: 'billing.billing_cycle_in_terms.two_years',
    [BILLING_CYCLE.THREE_YEARS]: 'billing.billing_cycle_in_terms.three_years',
    [BILLING_CYCLE.PAY_AS_YOU_GO]: 'billing.pay_you_go_lowercase',
    [BILLING_CYCLE.PAY_PER_ATTENDEE]: 'billing.billing_cycle_in_terms.pay_per_attendee',
    [BILLING_CYCLE.ONE_TIME]: 'billing.billing_cycle_in_terms.one_time',
  };

  return getBillingCycleTextWithValidation(billingCycle, billingCycleTextMap, 'getBillingCycleText');
};

/**
 * Get billing cycle description in subscription format (e.g., "Monthly subscription", "Annual subscription")
 * Used in order summary, checkout, and detailed subscription information
 * @param {string} billingCycle - Billing cycle enum value
 * @returns {string} Translated subscription cycle description or empty string
 *
 * @example
 * getSubscriptionCycleDesc('monthly')  // Returns: "Monthly subscription"
 * getSubscriptionCycleDesc('annual')   // Returns: "Annual subscription"
 * getSubscriptionCycleDesc('invalid')  // Returns: "" (with warning in console)
 */
export const getSubscriptionCycleDesc = (billingCycle) => {
  const subscriptionCycleDescs = {
    [BILLING_CYCLE.MONTHLY]: 'billing.order_summary.monthly_subscription',
    [BILLING_CYCLE.ANNUAL]: 'billing.order_summary.annual_subscription',
    [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: 'billing.order_summary.annual_term_monthly_subscription',
    [BILLING_CYCLE.ONE_MONTH]: 'billing.billing_cycle.one_month_new',
    [BILLING_CYCLE.QUARTERLY]: 'billing.order_summary.quarterly_subscription',
    [BILLING_CYCLE.TWO_YEARS]: 'billing.order_summary.two_years_subscription',
    [BILLING_CYCLE.THREE_YEARS]: 'billing.order_summary.three_years_subscription',
    [BILLING_CYCLE.PAY_AS_YOU_GO]: 'billing.pay_you_go',
    [BILLING_CYCLE.PAY_PER_ATTENDEE]: 'billing.billing_cycle.pay_per_attendee',
    [BILLING_CYCLE.ONE_TIME]: 'billing.billing_cycle.one_time',
  };

  return getBillingCycleTextWithValidation(billingCycle, subscriptionCycleDescs, 'getSubscriptionCycleDesc');
};
export const getNewPlanUnitWithQuantity = (unit, quantity) => {
  switch (unit) {
    case planUnitMap.user:
      return translation('plan.unit_with_quantity.user', quantity, { named: { quantity } });
    case planUnitMap.license:
      return translation('plan.unit_with_quantity.license', quantity, { named: { quantity } });
    case planUnitMap.room:
      return translation('plan.unit_with_quantity.room', quantity, { named: { quantity } });
    case planUnitMap.port:
      return translation('plan.unit_with_quantity.port', quantity, { named: { quantity } });
    case planUnitMap.credits:
      return translation('plan.unit_with_quantity.credits', quantity, { named: { quantity } });
    case planUnitMap.number:
      return translation('plan.unit_with_quantity.number', quantity, { named: { quantity } });
    case planUnitMap.space:
      return translation('plan.unit_with_quantity.space', quantity, { named: { quantity } });
    case planUnitMap.account:
      return translation('plan.unit_with_quantity.account', quantity, { named: { quantity } });
    case planUnitMap.membership:
      return translation('plan.unit_with_quantity.membership', quantity, { named: { quantity } });
    case planUnitMap.host:
      return translation('plan.unit_with_quantity.host', quantity, { named: { quantity } });
    case planUnitMap.calling:
      return translation('plan.unit_with_quantity.calling', quantity, { named: { quantity } });
    case planUnitMap.hub_host:
      return translation('plan.unit_with_quantity.hub_host', quantity, { named: { quantity } });
    case planUnitMap.co_editor:
      return translation('plan.unit_with_quantity.co_editor', quantity, { named: { quantity } });
    case planUnitMap.participant:
      return translation('plan.unit_with_quantity.participant', quantity, { named: { quantity } });
    case planUnitMap.attendee:
      return translation('plan.unit_with_quantity.attendee', quantity, { named: { quantity } });
    case planUnitMap.perMinute:
      return translation('billing.common.per_minute');
    default:
      return '';
  }
}

export const getPlanUnitByQuantity = (unit, quantity) => {
  switch (unit) {
    case planUnitMap.user:
      return translation('plan.unit.user', quantity);
    case planUnitMap.license:
      return translation('plan.unit.license', quantity);
    case planUnitMap.room:
      return translation('plan.unit.room', quantity);
    case planUnitMap.port:
      return translation('plan.unit.port', quantity);
    case planUnitMap.credits:
      return translation('plan.unit.credits', quantity);
    case planUnitMap.number:
      return translation('plan.unit.number', quantity);
    case planUnitMap.localNumber:
      return translation('plan.unit.localNumber', quantity);
    case planUnitMap.tollFreeNumber:
      return translation('plan.unit.tollFreeNumber', quantity);
    case planUnitMap.space:
      return translation('plan.unit.space', quantity);
    case planUnitMap.account:
      return translation('plan.unit.account', quantity);
    case planUnitMap.membership:
      return translation('plan.unit.membership', quantity);
    case planUnitMap.host:
      return translation('plan.unit.host', quantity);
    case planUnitMap.hub_host:
      return translation('plan.unit.hub_host', quantity);
    case planUnitMap.co_editor:
      return translation('plan.unit.co_editor', quantity);
    case planUnitMap.calling:
      return translation('plan.unit.calling', quantity);
    case planUnitMap.participant:
      return translation('plan.unit.participant', quantity);
    case planUnitMap.attendee:
      return translation('plan.unit.attendee', quantity);
    case planUnitMap.perMinute:
      return translation('billing.common.per_minute');
    default:
      return translation('plan.unit.license', quantity);
  }
}

/**
 * Check if a plan unit is a valid enum value
 *
 * Unit values are enum-based (0, 1, 2, etc.) from planUnitMap.
 * This function validates that the provided unit is one of the valid enum values.
 *
 * @param {number|undefined|null} unit - Plan unit enum value to validate
 * @returns {boolean} True if unit is a valid enum value from planUnitMap, false otherwise
 *
 * @example
 * isValidPlanUnit(0) // true (License enum)
 * isValidPlanUnit(1) // true (User enum)
 * isValidPlanUnit(9) // true (Calling enum)
 * isValidPlanUnit(999) // false (not in planUnitMap)
 * isValidPlanUnit(undefined) // false
 * isValidPlanUnit(null) // false
 */
export const isValidPlanUnit = (unit) => {
  return Object.values(planUnitMap).includes(unit);
}

const createPriceWrapper = (amount) => {
  const price = transferMoney(amount, { forceDiff: true });
  return `<span class="opc-price-total-bill">${price}</span>`;
};

export const getMergedBilledText = (billingCycle, amount, unit) => {
  const perUnitText = getPlanPerUnitDesc(unit);
  const perMonthBilledText = unit === planUnitMap.credits ? '' : translation('billing.common.per_billing_cycle_per_month');

  // Define billing cycle configurations
  const billingConfigs = {
    [BILLING_CYCLE.ONE_MONTH]: {
      unitSuffix: '',
      billingText: () => `(${translation('billing.bill_one_time')})`
    },
    [BILLING_CYCLE.ONE_TIME]: {
      unitSuffix: '',
      billingText: () => `(${translation('billing.bill_one_time')})`
    },
    [BILLING_CYCLE.MONTHLY]: {
      unitSuffix: perMonthBilledText,
      billingText: () => `(${translation('billing.bill_monthly')})`
    },
    [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: {
      unitSuffix: perMonthBilledText,
      billingText: () => `(${translation('billing.bill_12_payments')}, ${translation('billing.bill_monthly')})`
    },
    [BILLING_CYCLE.ANNUAL]: {
      unitSuffix: perMonthBilledText,
      billingText: () => `(${createPriceWrapper(amount)} ${translation('billing.bill_annually')})`
    },
    [BILLING_CYCLE.QUARTERLY]: {
      unitSuffix: perMonthBilledText,
      billingText: () => `(${createPriceWrapper(amount)} ${translation('billing.bill_quarterly')})`
    },
    [BILLING_CYCLE.TWO_YEARS]: {
      unitSuffix: perMonthBilledText,
      billingText: () => `(${createPriceWrapper(amount)} ${translation('billing.bill_two_years')})`
    },
    [BILLING_CYCLE.THREE_YEARS]: {
      unitSuffix: perMonthBilledText,
      billingText: () => `(${createPriceWrapper(amount)} ${translation('billing.bill_three_years')})`
    }
  };

  const config = billingConfigs[billingCycle];
  if (!config) {
    return perUnitText;
  }

  const unitPart = config.unitSuffix ? `${perUnitText} ${config.unitSuffix}` : perUnitText;
  const billingPart = config.billingText();

  return `${unitPart} ${billingPart}`;
}

/**
 * Get capacity description for plans that have participants/attendees
 * Uses plan's capacityUnit metadata to determine the unit type
 * @param {Object} plan - Plan object with optional fields:
 *   - participants/capacity: Number of participants/attendees
 *   - capacityDesc: Pre-formatted capacity string (e.g., "1M" from WEBINAR pre.js)
 *   - capacityUnit: Unit type from planUnitMap (attendee or participant)
 *   - extra.cartCapacity: CMR-specific formatted capacity value (e.g., 200, 1, 5)
 *   - extra.cartCapacityUnit: CMR-specific unit string (e.g., "GB", "TB")
 * @returns {string|null} - Formatted capacity string or null if not applicable
 */
export const getPlanCapacityDesc = (plan) => {
  if (!plan) {
    return null;
  }

  // Special handling for CMR plans with cartCapacity/cartCapacityUnit
  if (plan.extra?.cartCapacity !== undefined && plan.extra?.cartCapacityUnit) {
    return `${plan.extra.cartCapacity} ${plan.extra.cartCapacityUnit}`;
  }

  // Support both 'participants' and 'capacity' fields
  const capacity = plan.participants || plan.capacity;
  if (!capacity) {
    return null;
  }

  // Prefer using pre-formatted capacityDesc (e.g., from WEBINAR pre.js)
  // If not available, format the capacity value using capacityFormatter
  // capacityFormatter handles special cases like 1000000 → "1M"
  const formattedCapacity = plan.capacityDesc || capacityFormatter(capacity);

  // Use plan's capacityUnit metadata to determine the unit type
  // This is set in each plan's pre.js file (e.g., planUnitMap.attendee or planUnitMap.participant)
  // Fallback to 'participant' if not specified
  const unit = plan.capacityUnit || planUnitMap.participant;

  // Use raw capacity number for plural logic, but formattedCapacity for display
  // translation(key, pluralCount, { named: { quantity } })
  // - pluralCount: used for plural form selection (1 vs many)
  // - named.quantity: used for template {quantity} placeholder
  switch (unit) {
    case planUnitMap.attendee:
      return translation('plan.unit_with_quantity.attendee', capacity, { named: { quantity: formattedCapacity } });
    case planUnitMap.participant:
      return translation('plan.unit_with_quantity.participant', capacity, { named: { quantity: formattedCapacity } });
    default:
      return getNewPlanUnitWithQuantity(unit, capacity);
  }
}
